import type { SocialMedia, SocialProfile } from './types';
import type { StoryDesign } from './story-design';

export const STORY_LIFETIME_MS = 24 * 60 * 60 * 1000;
export const STORY_TEXT_LIMIT = 500;
export const STORY_PEOPLE_LIMIT = 100;
export const STORY_AUDIENCES = ['everyone', 'followers', 'close_friends', 'selected'] as const;
export type StoryAudience = typeof STORY_AUDIENCES[number];
export const STORY_BACKGROUNDS = { charcoal: '#18181b', ocean: '#164e63', forest: '#14532d', plum: '#581c87', rose: '#881337' } as const;
export type StoryBackground = keyof typeof STORY_BACKGROUNDS;
export type StoryAudienceInput = { audience: StoryAudience; selectedIds: string[]; hiddenIds: string[] };
export type StorySettings = { defaultAudience: StoryAudience; closeFriendIds: string[]; hiddenIds: string[] };
export const DEFAULT_STORY_SETTINGS: StorySettings = { defaultAudience: 'followers', closeFriendIds: [], hiddenIds: [] };
export const STORY_AUDIENCE_LABELS: Record<StoryAudience, string> = { everyone: 'Everyone', followers: 'Followers', close_friends: 'Close friends', selected: 'Selected people' };
export type SocialStory = {
  id: string;
  author: SocialProfile;
  body: string;
  media: SocialMedia | null;
  audience: StoryAudience;
  background: StoryBackground;
  createdAt: string;
  expiresAt: string;
  seen: boolean;
  audienceSettings?: StoryAudienceInput;
  design?: StoryDesign | null;
  revision?: number;
  assets?: SocialMedia[];
};
export type StoryFeed = { stories: SocialStory[]; nextCursor: string | null };

// Missing/invalid expirations and unknown audiences fail closed, even for the owner.
export function canReadStory(input: {
  viewerId: string | null; authorId: string; privateAccount: boolean; acceptedFollow: boolean; blocked: boolean;
  audience: string; selectedIds: string[]; hiddenIds: string[]; closeFriendIds: string[];
  expiresAt: string; deleted: boolean; now?: number;
}) {
  const { viewerId, authorId } = input;
  if (!viewerId || input.deleted || !(Date.parse(input.expiresAt) > (input.now ?? Date.now())) || !STORY_AUDIENCES.includes(input.audience as StoryAudience)) return false;
  if (viewerId === authorId) return true;
  if (input.blocked || input.hiddenIds.includes(viewerId) || input.privateAccount && !input.acceptedFollow) return false;
  return input.audience === 'everyone' || input.audience === 'followers' && input.acceptedFollow
    || input.audience === 'close_friends' && input.closeFriendIds.includes(viewerId)
    || input.audience === 'selected' && input.selectedIds.includes(viewerId);
}
